import { useEffect, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { ActionButton } from './ActionButton.js'
import { notify, notifyError } from './Toasts.js'
import { api, type TenantUser } from '../net/apiClient.js'
import { uuidv7 } from '../net/ids.js'

/**
 * Die Angaben, die der Person gehören – nicht dem Betrieb.
 *
 * Ein Betrieb hat eine offizielle Anschrift, eine offizielle Nummer und eine
 * offizielle Mailadresse. Die stehen auf jeder Rechnung und gehören der Firma.
 * Sie stehen in den Einstellungen unter «Firma».
 *
 * Hier steht das andere: wie die Person heisst und unter welcher Nummer sie
 * erreichbar ist. Das steht auf keinem Beleg.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum das zwei Bildschirme sind und nicht einer
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Weil ein einziges Formular mit «Telefon» zweimal darin eine Falle ist. Wer
 * seine Handynummer in das falsche Feld tippt, merkt es nicht – er merkt es,
 * wenn ein Kunde ein halbes Jahr später auf seinem privaten Handy anruft,
 * weil die Nummer auf einer Rechnung stand.
 *
 * Die Mailadresse steht hier bewusst nur zum Lesen. An ihr hängen die
 * Anmeldung und der zweite Faktor; sie im Vorbeigehen zu tauschen wäre der
 * Weg, auf dem jemand ein übernommenes Konto dauerhaft behält. Wer sie ändern
 * muss, wendet sich an die Betriebsleitung.
 */
export function MeinProfil({ session }: { session: SessionUser }): JSX.Element {
  const [name, setName] = useState(session.name ?? '')
  const [phone, setPhone] = useState('')
  const [geladen, setGeladen] = useState(false)

  useEffect(() => {
    /*
      Die eigene Nummer kommt aus der Benutzerliste – es gibt keinen eigenen
      Weg dafür, und für einen einzigen Wert lohnt sich keiner. Wer die Liste
      nicht sehen darf, bekommt hier ein leeres Feld und kann trotzdem
      schreiben; das ist richtig so.
    */
    void api
      .listUsers()
      .then((r) => {
        const ich = (r.users as TenantUser[]).find((u) => u.userId === session.userId)
        if (ich?.phone) setPhone(ich.phone)
      })
      .catch(() => undefined)
      .finally(() => setGeladen(true))
  }, [session.userId])

  return (
    <div className="o-card">
      <div className="o-card-head">
        <h3>Meine Angaben</h3>
        <span className="o-soft">Gehören dir, nicht dem Betrieb</span>
      </div>
      <div className="o-pad">
        <p className="o-soft" style={{ marginTop: 0 }}>
          Diese Angaben stehen auf keiner Rechnung und auf keiner Offerte. Dort steht die offizielle
          Anschrift des Betriebs — zu finden unter <strong>Firma</strong>.
        </p>

        <div className="o-grid o-grid-2" style={{ marginTop: 16 }}>
          <div className="o-field">
            <label htmlFor="profil-name">Name</label>
            <input
              autoComplete="off"
              id="profil-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="o-field">
            <label htmlFor="profil-phone">Meine Telefonnummer</label>
            <input
              autoComplete="off"
              id="profil-phone"
              inputMode="tel"
              value={phone}
              placeholder={geladen ? '' : 'Wird geladen …'}
              onChange={(e) => setPhone(e.target.value)}
            />
            <span className="hint">Für den Betrieb, nicht für den Kunden.</span>
          </div>
        </div>

        <div className="o-grid o-grid-2" style={{ marginTop: 12 }}>
          <div className="o-field">
            <label htmlFor="profil-mail">Meine Anmeldeadresse</label>
            <input
              autoComplete="off"
              id="profil-mail"
              value={session.email ?? ''}
              readOnly
              disabled
            />
            <span className="hint">
              Daran hängen Anmeldung und zweiter Faktor. Änderung nur über die Betriebsleitung.
            </span>
          </div>
        </div>

        <div className="o-aktionen" style={{ marginTop: 16 }}>
          <ActionButton
            id="btn-profil-speichern"
            labels={{ idle: 'Speichern', busy: 'Speichert …', success: 'Gespeichert' }}
            onAction={async () => {
              if (!name.trim()) {
                notify.warn('Ohne Namen geht es nicht.')
                return
              }
              try {
                await api.profilSetzen({ name: name.trim(), phone: phone.trim() || null }, uuidv7())
                notify.ok('Gespeichert.')
              } catch (e) {
                notifyError(e)
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}
